import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPhone,
  IconPlugConnected,
  IconCalendarEvent,
  IconUsers,
} from '@tabler/icons-react';
import KpiCard from './KpiCard';
import KpiCardSkeleton from './KpiCardSkeleton';
import type { DashboardOverviewKpis } from '@types';

interface KpiStripProps {
  kpis: DashboardOverviewKpis | null;
  isLoading: boolean;
  prevPeriod: { from: Date; to: Date } | null;
  /** Opens the sessions list filtered to the sessions this tile counts. */
  onActiveSessionsClick?: () => void;
}

const KPI_CONFIG = [
  { key: 'totalCalls' as const, i18nKey: 'dashboard.overview.kpi.totalCalls', icon: <IconPhone size={18} />, formatType: 'number' as const },
  { key: 'connectRate' as const, i18nKey: 'dashboard.overview.kpi.connectRate', icon: <IconPlugConnected size={18} />, formatType: 'percent' as const },
  { key: 'appointmentsSet' as const, i18nKey: 'dashboard.overview.kpi.appointments', icon: <IconCalendarEvent size={18} />, formatType: 'number' as const },
  { key: 'activeSessions' as const, i18nKey: 'dashboard.overview.kpi.activeSessions', icon: <IconUsers size={18} />, formatType: 'number' as const },
] as const;

const KPI_GRID = 'grid grid-cols-2 md:grid-cols-4 gap-3';

export default function KpiStrip ({ kpis, isLoading, prevPeriod, onActiveSessionsClick }: KpiStripProps): React.JSX.Element {
  const { t } = useTranslation();

  if (isLoading || kpis === null) {
    return (
      <div className={KPI_GRID}>
        {/* Driven by KPI_CONFIG so the skeleton can never show a different
            number of cards than the strip it stands in for. */}
        {KPI_CONFIG.map(({ key }) => (
          <KpiCardSkeleton key={key} />
        ))}
      </div>
    );
  }

  return (
    <div className={KPI_GRID}>
      {KPI_CONFIG.map(({ key, i18nKey, icon, formatType }) => (
        <KpiCard
          key={key}
          label={t(i18nKey)}
          icon={icon}
          kpiValue={kpis[key]}
          formatType={formatType}
          prevPeriod={prevPeriod}
          isLive={key === 'activeSessions'}
          // Only this tile drills in: it is the one whose number you cannot
          // reach any other way, because it ignores the date range the
          // sessions list is filtered by.
          onClick={key === 'activeSessions' ? onActiveSessionsClick : undefined}
        />
      ))}
    </div>
  );
}
